<script setup>
import { onMounted, computed, onUnmounted } from 'vue'
import { useStore } from 'vuex'

import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import MapboxDraw from '@mapbox/mapbox-gl-draw'

const store = useStore()
const draw = computed(() => store.getters['geo/getDraw'])
const props = defineProps({
  map: {
    type: Object,
    required: true
  }
})

onMounted(() => {
  if (props.map.hasControl(draw.value)) {
    return
  }

  const drawInstance = new MapboxDraw({
    displayControlsDefault: false,
    userProperties: true,
    // Optimized styles - reduced layers and simplified rendering
    styles: [
      {
        'id': 'highlight-active-points',
        'type': 'circle',
        'filter': ['all',
          ['==', '$type', 'Point'],
          ['==', 'meta', 'feature'],
          ['==', 'active', 'true']
        ],
        'paint': {
          'circle-radius': 12,
          'circle-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#fff',
        }
      },
      // Removed redundant outline box layer
      {
        'id': 'points-are-blue',
        'type': 'circle',
        'filter': ['all',
          ['==', '$type', 'Point'],
          ['==', 'meta', 'feature'],
          ['==', 'active', 'false']
        ],
        'paint': {
          'circle-radius': 10,
          'circle-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#fff'
        }
      },
      // ACTIVE (being drawn)
      // line stroke
      {
        'id': 'gl-draw-line',
        'type': 'line',
        'filter': ['all',
          ['==', '$type', 'LineString'],
          ['==', 'active', 'true']
        ],
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'line-dasharray': [0.2, 2],
          'line-width': 4
        }
      },
      // polygon fill
      {
        'id': 'gl-draw-polygon-fill',
        'type': 'fill',
        'filter': ['all',
          ['==', '$type', 'Polygon'],
          ['==', 'active', 'true']
        ],
        'paint': {
          'fill-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'fill-outline-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'fill-opacity': 0.1
        }
      },
      // polygon outline stroke
      {
        'id': 'gl-draw-polygon-stroke-active',
        'type': 'line',
        'filter': ['all',
          ['==', '$type', 'Polygon'],
          ['==', 'active', 'true']
        ],
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'line-dasharray': [0.2, 2],
          'line-width': 4
        }
      },
      // Combined vertex points style
      {
        'id': 'gl-draw-points-vertices',
        'type': 'circle',
        'filter': ['all',
          ['==', 'meta', 'vertex'],
          ['==', '$type', 'Point']
        ],
        'paint': {
          'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 8, 7],
          'circle-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'circle-stroke-width': ['case', ['==', ['get', 'active'], 'true'], 3, 2],
          'circle-stroke-color': '#fff'
        }
      },
      // INACTIVE (static, already drawn)
      // line stroke
      {
        'id': 'gl-draw-line-static',
        'type': 'line',
        'filter': ['all',
          ['==', '$type', 'LineString'],
          ['==', 'active', 'false']
        ],
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'line-width': 2
        }
      },
      // polygon fill
      {
        'id': 'gl-draw-polygon-fill-static',
        'type': 'fill',
        'filter': ['all',
          ['==', '$type', 'Polygon'],
          ['==', 'active', 'false']
        ],
        'paint': {
          'fill-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'fill-outline-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'fill-opacity': 0.1
        }
      },
      // polygon outline
      {
        'id': 'gl-draw-polygon-stroke-static',
        'type': 'line',
        'filter': ['all',
          ['==', '$type', 'Polygon'],
          ['==', 'active', 'false']
        ],
        'layout': {
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': ['case', ['has', 'user_color'], ['get', 'user_color'], '#0059ff'],
          'line-width': 2
        }
      }
    ]
  })

  props.map.addControl(drawInstance, 'bottom-right')
  store.commit('geo/SET_DRAW', drawInstance)
})
</script>

<template>
  <slot></slot>
</template>
